import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import type { User } from '../common/user';
import { removeAccessToken } from '../lib/utils/tokens';
import { getProfileOptions } from '../queries/users/profile';
import { useKeycloak } from './KeycloakContext';

interface AppContextType {
	selectedAssetId: string | null;
	setSelectedAssetId: (id: string | null) => void;
	onLogout: () => void;
	profile: User | null;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppContextProvider({ children }: { children: ReactNode }) {
	const navigate = useNavigate();
	const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
	const { keycloak, authenticated } = useKeycloak();
	const { data: profileResponse } = useQuery(getProfileOptions({ enabled: authenticated }));
	const profile = profileResponse?.data || null;

	const onLogout = async () => {
		if (keycloak && authenticated) {
			await keycloak.logout().catch((error) => {
				console.error('Logout failed:', error);
			});
		}
		setSelectedAssetId(null);
		removeAccessToken();
		// Navigate to dashboard or another page
		navigate('/login');
	};

	return (
		<AppContext.Provider
			value={{
				selectedAssetId,
				setSelectedAssetId,
				onLogout,
				profile,
			}}
		>
			{children}
		</AppContext.Provider>
	);
}

export const useAppContext = () => {
	const context = useContext(AppContext);
	if (!context) throw new Error('useAppContext must be used within a AppContextProvider');
	return context;
};

export default AppContext;
